/**
 * The auth bundle's composition root: it builds the real client and publishes `window.urcAuth`.
 *
 * Deliberately thin and deliberately untested. Everything with a decision in it lives in
 * ./runtime.js, ./store.js, ./state.js, ./returnTo.js and ./config.js, all of which take their
 * world as arguments and all of which have unit tests. What is left here is wiring, and wiring is
 * what an end-to-end check covers.
 *
 * WHY A GLOBAL AND NOT AN IMPORT
 * ------------------------------
 * `window.urcAuth` is the contract the `app` and `loyalty` bundles read. Neither may import from
 * here, and that is [D-055](../../../docs/decisions.md): the three entries share one Rollup build,
 * so an import would hoist a shared chunk and rewrite the module graph of the already-shipped
 * loyalty bundle — the bundle that carries the fan's credential. A separate entry plus a global
 * creates no shared chunk.
 *
 * It is also strictly better than what it replaces. The same rule was previously discharged by
 * copy-pasting the cookie reader: five implementations of "read an `auth0_*` cookie" shipped on
 * every page with four different semantics, and four of them carried a bug that reported "signed
 * out" for anyone holding a duplicate cookie.
 *
 * ORDERING
 * --------
 * `App\Auth\Assets` enqueues this before `app` and `loyalty`, and `app/filters.php` rewrites all of
 * them to `type="module"` — so they are deferred and execute in document order, which makes this
 * the first of the three to run. Consumers still must not read `state()` synchronously at import:
 * the SDK answers asynchronously, so `ready` and `subscribe` are the contract.
 */

import { Auth0Client } from '@auth0/auth0-spa-js';

import { NO_FAN } from './state.js';
import { createAuthStore } from './store.js';
import { createAuthRuntime } from './runtime.js';
import { isConfigured, isDebug, readConfig } from './config.js';
import { isLoginHref, returnToFrom, shouldIntercept } from './loginLinks.js';

/** Where the state is published for anyone reading the DOM instead of the console. */
const STATE_ATTRIBUTE = 'data-auth';

/** Channel other tabs announce a change on, so this one re-checks rather than guessing. */
const CHANNEL = 'urc-auth';

/**
 * Publish the state on `<html>`.
 *
 * The same idiom as `data-loyalty-consent`, `data-loyalty-toast` and `data-score-tracker`, and
 * deliberately the same vocabulary as `outlier-stats` uses, so there is one debugging language
 * across both sites.
 *
 * @param {Object|null} state The AuthState, or null before the SDK answers.
 * @return {void}
 */
function publish( state ) {
	if ( typeof document === 'undefined' || ! document.documentElement ) {
		return;
	}

	let value = 'resolving';

	if ( state ) {
		value = state.signedIn ? 'signed-in' : 'signed-out';
	}

	document.documentElement.setAttribute( STATE_ATTRIBUTE, value );
}

/**
 * `sessionStorage`, or null where it is unavailable.
 *
 * **The store falls back to a null storage when this returns null, and that fallback is not
 * harmless — which is why this function exists.** `store.js` compares the fan against the *owner
 * record* rather than against the last value the page saw, precisely so that the first settle of a
 * fresh document is an identity change when the previous fan's data is still in `sessionStorage`.
 * With no storage every read answers null, which normalises to "nobody owns this tab", so
 * `switched` is true for any signed-in fan on **every page load** and `onIdentityChange` fires
 * every time. That is the safe direction for a store on its own, and it is the wrong direction for
 * the reset fan-out hung off it: a per-load reset drops the toast snapshot on a page that had a
 * perfectly good one, and `reconcileLoyaltyToasts` answers a missing snapshot by silently
 * re-baselining — so the fan is never toasted again.
 *
 * Touched rather than merely referenced: browsers configured to block site data throw on ACCESS,
 * not on use, so trying is the only way to know. Same guard, for the same reason, as
 * `resources/scripts/loyalty/consent/index.js`'s `safeSessionStorage()`.
 *
 * @return {Object|null} The storage, or null.
 */
function safeSessionStorage() {
	try {
		const storage = window.sessionStorage;

		storage.getItem( 'urc.auth.probe' );

		return storage;
	} catch ( error ) {
		return null;
	}
}

/**
 * A `window.urcAuth` that answers honestly on an install with no Auth0 configured.
 *
 * Consumers get the full shape rather than `undefined`, so a fresh clone and an install whose
 * `AUTH0_DOMAIN` is unset behave like the pre-Auth0 site instead of throwing on every page.
 *
 * @return {Object} The contract, answering "nobody" to everything.
 */
function inertAuth() {
	const state = { ...NO_FAN };

	return {
		ready: Promise.resolve( state ),
		state: () => ( { ...state } ),
		claims: () => null,
		subscribe: listener => {
			listener( { ...state } );

			return () => {};
		},
		onIdentityChange: () => () => {},
		getAccessToken: () => Promise.resolve( null ),
		getIdToken: () => Promise.resolve( null ),
		login: () => Promise.resolve(),
		logout: () => Promise.resolve(),
	};
}

/**
 * Re-ask the SDK whenever the browser suggests the answer may have moved.
 *
 * `focus` and `visibilitychange` catch a sign-in or sign-out that happened elsewhere; `pageshow`
 * catches a bfcache restore, which replays neither `DOMContentLoaded` nor, on some engines,
 * `visibilitychange` — the same gap `toasts/trigger.js` covers for its own state.
 *
 * **This is not the old probe.** Re-checking is now free of consequence: a failed check settles
 * "nobody is signed in in this browser" and clears nothing, where `performSilentCheck(true)` on
 * these same two events destroyed a live session. See ./runtime.js.
 *
 * @param {Function} resolve The runtime's resolve().
 * @return {void}
 */
function watchForChanges( resolve ) {
	const recheck = () => {
		resolve().catch( () => {} );
	};

	window.addEventListener( 'focus', recheck );

	// `pageshow` fires on an ordinary load too, where the initial resolve() has already run -- so
	// only a bfcache restore is interesting here. `toasts/trigger.js` guards its own `pageshow` the
	// same way, for the same reason. Without this a plain page load resolved two or three times.
	window.addEventListener( 'pageshow', event => {
		if ( event.persisted ) {
			recheck();
		}
	} );
	document.addEventListener( 'visibilitychange', () => {
		if ( ! document.hidden ) {
			recheck();
		}
	} );

	if ( typeof BroadcastChannel === 'undefined' ) {
		return;
	}

	try {
		const channel = new BroadcastChannel( CHANNEL );

		// Announce, so a switch in this tab reaches the others; and listen, so a switch in another
		// tab reaches this one. Both sides re-ask the SDK rather than trusting the message, which
		// is what stops a forged or stale broadcast deciding anything.
		channel.addEventListener( 'message', recheck );
		window.addEventListener( 'urc-auth:changed', () => {
			try {
				channel.postMessage( { type: 'changed' } );
			} catch ( error ) {
				// A closed channel. Nothing to do.
			}
		} );
	} catch ( error ) {
		// No channel. Every tab still re-checks on focus, which is the fallback that always works.
	}
}

/**
 * Start a login from the page the fan is on, rather than from `/auth/login`.
 *
 * One delegated listener, so it follows the header's reconcile rewriting those anchors instead of
 * fighting it -- ./loginLinks.js carries why the href has to be read at click time. Everything this
 * cannot serve falls through to the anchor, which is a working route.
 *
 * @param {Function} login    The runtime's login().
 * @param {string}   loginUrl The login route, from the published config.
 * @return {void}
 */
function enhanceLoginLinks( login, loginUrl ) {
	if ( loginUrl === '' || typeof document === 'undefined' ) {
		return;
	}

	// Two fast clicks would otherwise build two transactions -- the second overwriting the first
	// in `sessionStorage` -- and call `location.assign` twice. Cleared when a login rejects, and
	// on a bfcache restore -- see the `pageshow` listener below.
	let starting = false;

	// The anchor this marked busy, so a restore can unmark exactly that one rather than sweeping
	// every `aria-busy` on the page. Null whenever no login is pending.
	let busyAnchor = null;

	/*
	 * Let go of a pending login when the fan comes back to this document.
	 *
	 * "A successful login always ends in leaving the document" is true, and it is not the same
	 * thing as the document being gone. Chrome and Safari both put a page the fan left for Auth0
	 * into the back/forward cache, so Back restores THIS document with its module state intact --
	 * `starting` still true and the CTA still `aria-busy`. Every later click on a login link then
	 * reaches `event.preventDefault()` and returns at the guard below, so the control is dead for
	 * the life of the tab and only a reload revives it. Reported against `/loyalty-join/`, where
	 * the whole page is that one button; it is the header's three anchors too, because the latch
	 * is per-page rather than per-anchor.
	 *
	 * `persisted` only: an ordinary load evaluates this module afresh, where `starting` is already
	 * false. Same guard, for the same reason, as watchForChanges() above.
	 */
	window.addEventListener( 'pageshow', event => {
		if ( ! event.persisted ) {
			return;
		}

		starting = false;

		if ( busyAnchor ) {
			busyAnchor.removeAttribute( 'aria-busy' );
			busyAnchor = null;
		}
	} );

	document.addEventListener( 'click', event => {
		if ( typeof event.target?.closest !== 'function' ) {
			return;
		}

		const anchor = event.target.closest( 'a[href]' );

		if ( ! anchor || ! isLoginHref( anchor.href, loginUrl ) ) {
			return;
		}

		if (
			! shouldIntercept( {
				button: event.button,
				metaKey: event.metaKey,
				ctrlKey: event.ctrlKey,
				shiftKey: event.shiftKey,
				altKey: event.altKey,
				defaultPrevented: event.defaultPrevented,
				target: anchor.getAttribute( 'target' ) || '',
			} )
		) {
			return;
		}

		event.preventDefault();

		// A second click while the first is still building its `/authorize` URL is
		// cancelled and dropped, NOT handed back to the browser: letting it through would
		// race a navigation to the shell against the redirect already in flight, and land
		// some fans on the page this exists to skip.
		if ( starting ) {
			return;
		}

		const href = anchor.href;

		starting = true;

		// `loginWithRedirect()` awaits PKCE before it calls `location.assign`, and the click is
		// already cancelled -- so without this there are tens of milliseconds plus a whole
		// `/authorize` navigation in which the fan has no indication that anything is happening.
		// Set from script rather than from _header.scss, where every rule compiles under
		// `.banner .navbar` and one of the two buttons sits outside it. See docs/subsystems/header.md.
		anchor.setAttribute( 'aria-busy', 'true' );
		busyAnchor = anchor;

		Promise.resolve( login( returnToFrom( href ) ) ).catch( () => {
			// **Not swallowed**, unlike resources/scripts/loyalty/urcAuth.js's login wrapper, where a
			// rejection is a click that visibly does nothing. Here the shell is still a working route,
			// so hand the navigation back to the href the fan actually clicked.
			starting = false;
			busyAnchor = null;
			anchor.removeAttribute( 'aria-busy' );
			window.location.assign( href );
		} );
	} );
}

const config = readConfig( typeof window === 'undefined' ? null : window.urcAuth0 );

if ( typeof window !== 'undefined' ) {
	if ( ! isConfigured( config ) ) {
		window.urcAuth = inertAuth();
		publish( { ...NO_FAN } );
	} else {
		const debug = isDebug( config );
		// The storage is passed explicitly. Omitting it takes store.js's null-storage default,
		// which never remembers an owner -- see safeSessionStorage() for what that costs.
		const store = createAuthStore( { storage: safeSessionStorage() || undefined } );

		if ( debug ) {
			// eslint-disable-next-line no-console
			console.info( '[auth] config', config );
		}

		const client = new Auth0Client( {
			domain: config.domain,
			clientId: config.clientId,
			// No `audience` here. Per call only -- ./runtime.js carries the reason.
			authorizationParams: { redirect_uri: config.callbackUrl },
			useRefreshTokens: config.useRefreshTokens,
			useRefreshTokensFallback: config.useRefreshTokens,
			cacheLocation: config.cacheLocation,
		} );

		const runtime = createAuthRuntime( {
			client,
			store,
			config,
			navigate: target => window.location.replace( target ),
			// eslint-disable-next-line no-console
			warn: ( ...args ) => console.warn( ...args ),
			log: debug
				? ( message, detail ) =>
						// eslint-disable-next-line no-console
						console.info( `[auth] ${ message }`, detail === undefined ? '' : detail )
				: () => {},
		} );

		publish( null );

		store.subscribe( state => {
			publish( state );
			window.dispatchEvent( new CustomEvent( 'urc-auth:changed', { detail: state } ) );
		} );

		window.urcAuth = {
			ready: store.ready,
			state: store.state,
			claims: store.claims,
			subscribe: store.subscribe,
			onIdentityChange: store.onIdentityChange,
			getAccessToken: runtime.getAccessToken,
			getIdToken: runtime.getIdToken,
			login: runtime.login,
			logout: runtime.logout,
		};

		// `action` is published by App\Auth\Routes for its three shell pages and is absent
		// everywhere else. Reading it beats sniffing the path: the routes are registered on
		// `parse_request` and their URLs are the server's to decide, not this file's.
		const action = typeof window.urcAuth0.action === 'string' ? window.urcAuth0.action : '';

		if ( action === 'callback' ) {
			runtime.handleCallback();
		} else if ( action === 'login' ) {
			runtime.login( window.urcAuth0.returnTo );
		} else if ( action === 'logout' ) {
			runtime.logout( window.urcAuth0.returnTo );
		} else {
			runtime.resolve().catch( () => {} );
			watchForChanges( runtime.resolve );
			// Ordinary pages only. The three shells above are what this exists to skip.
			enhanceLoginLinks( runtime.login, config.loginUrl );
		}
	}
}
